import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/lib/theme";
import { Wrench, LogOut, Users, ClipboardList, UserCircle2, Building2, Settings, Trophy, KeyRound, History, LayoutGrid, LineChart, DatabaseBackup, Inbox } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { ShopSwitcher } from "@/components/ShopSwitcher";
import { SupportMenu } from "@/components/SupportMenu";
import { NoticeBanner } from "@/components/NoticeBanner";
import { HelpTip, helpForRoute } from "@/components/HelpTip";
import { ImpersonationBar, TrialBanner } from "@/components/PlanBanners";
import { ModuleGuard } from "@/components/ModuleGuard";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TermsGate } from "@/components/TermsGate";
import { AssistantFab } from "@/components/AssistantFab";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { shopLogoUrl } from "@/lib/api";
import { useState } from "react";
import { BrandTheme } from "@/components/BrandTheme";
import { OwnerTopNav } from "@/components/OwnerTopNav";

export function Shell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const role = user?.role;
  const isSuper = role === "super_admin";
  const isOwner = role === "owner";

  const coreNavItems = isSuper
    ? [{ to: "/admin", label: "Oficinas", icon: Building2, testid: "nav-shops" },
       { to: "/admin/backups", label: "Backups", icon: DatabaseBackup, testid: "nav-backups" },
       { to: "/admin/demonstracoes", label: "Demonstrações", icon: Inbox, testid: "nav-demo-requests" }]
    : isOwner
    ? [
        { to: "/owner", label: "Central", icon: LayoutGrid, testid: "nav-hub" },
        { to: "/owner/ordens", label: "Ordens", icon: ClipboardList, testid: "nav-orders" },
        { to: "/owner/customers", label: "Clientes", icon: Users, testid: "nav-customers" },
        { to: "/owner/dashboard", label: "Painel", icon: LineChart, testid: "nav-kpi" },
        { to: "/owner/settings", label: "Oficina", icon: Settings, testid: "nav-settings" },
      ]
    : [
        { to: "/cliente", label: "Minhas Ordens", icon: ClipboardList, testid: "nav-my-orders" },
        { to: "/cliente/clube", label: "Meus Pontos", icon: Trophy, testid: "nav-my-points" },
      ];
  const navItems = coreNavItems;

  const handleLogout = async () => {
    const slug = role === "customer" ? user?.shop?.slug : null;
    if (slug) {
      await logout();
      window.location.replace(`/o/${slug}`);
      return;
    }
    await logout();
    navigate("/login", { replace: true });
  };

  const shop = user?.shop;
  const shopLogo = shop?.logo_path ? `${shopLogoUrl(shop.id)}` : null;
  const [logoBroken, setLogoBroken] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <BrandTheme />
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 backdrop-blur-md bg-zinc-950/85 border-b border-zinc-800"
      >
        <div className={cn("mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between gap-4", isOwner && "lg:max-w-none lg:px-6")}>
          <Link
            to={isSuper ? "/admin" : isOwner ? "/owner" : "/cliente"}
            className="flex items-center gap-2 group"
            data-testid="brand-link"
            translate="no"
          >
            {shop?.name ? (
              <>
                {shopLogo && !logoBroken ? (
                  <img
                    src={shopLogo}
                    alt={shop?.name || "Logo da oficina"}
                    data-testid="shop-logo"
                    onError={() => setLogoBroken(true)}
                    className="h-10 w-10 object-contain shrink-0 rounded-md bg-white/5 ring-1 ring-white/10 p-0.5"
                  />
                ) : (
                  <picture data-testid="brand-logo-panel" className="inline-block">
                    <img src="/brand/autooficina-logo-white.png" alt="AutoOficina" className="h-10 w-auto shrink-0 drop-shadow-[0_0_14px_rgba(74,222,128,0.3)] hidden dark:block" />
                    <img src="/brand/autooficina-logo.png" alt="AutoOficina" className="h-10 w-auto shrink-0 drop-shadow-[0_0_14px_rgba(74,222,128,0.3)] dark:hidden" />
                  </picture>
                )}
                <span className="leading-tight block min-w-0">
                  <span className="font-display text-xl font-black uppercase tracking-wider text-zinc-100 block">
                    <span className="truncate max-w-[120px] sm:max-w-[220px] inline-block align-bottom">
                      {shop.name}
                    </span>
                  </span>
                  <span className="hidden sm:block text-[10px] font-mono-caps text-zinc-500">
                    <span>
                      {isSuper ? "Super administrador" : isOwner ? "Gestão da oficina" : "Área do cliente"}
                    </span>
                  </span>
                </span>
              </>
            ) : (
              <>
                <picture data-testid="brand-logo" className="inline-block">
                  <img src="/brand/autooficina-logo-white.png" alt="AutoOficina" className="h-10 sm:h-12 w-auto drop-shadow-[0_0_18px_rgba(74,222,128,0.35)] hidden dark:block" />
                  <img src="/brand/autooficina-logo.png" alt="AutoOficina" className="h-10 sm:h-12 w-auto drop-shadow-[0_0_18px_rgba(74,222,128,0.35)] dark:hidden" />
                </picture>
                <span className="leading-tight hidden sm:block">
                  <span className="text-[10px] font-mono-caps text-zinc-500 block">
                    {isSuper ? "Super administrador" : isOwner ? "Gestão da oficina" : "Área do cliente"}
                  </span>
                </span>
              </>
            )}
          </Link>

          <nav className={cn("hidden md:flex items-center gap-1", isOwner && "lg:hidden")}>
            {navItems.map(({ to, label, icon: Icon, testid }) => {
              const active = to === "/owner" || to === "/admin"
                ? location.pathname === to
                : to === "/owner/ordens"
                ? location.pathname.startsWith("/owner/ordens") || location.pathname.startsWith("/owner/veiculo")
                : to === "/cliente"
                ? location.pathname === "/cliente" || (location.pathname.startsWith("/cliente/") && !location.pathname.startsWith("/cliente/clube"))
                : location.pathname === to || location.pathname.startsWith(to + "/");
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    active
                      ? "bg-green-brand/15 text-green-300 border border-green-brand/40"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border border-transparent",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <ShopSwitcher />
            {isOwner && <SupportMenu />}
            <div className="hidden sm:flex flex-col items-end leading-tight" translate="no">
              <span className="text-xs text-zinc-500 font-mono-caps">
                <span>{isSuper ? "Super admin" : isOwner ? "Dono" : "Cliente"}</span>
              </span>
              <span className="text-sm text-zinc-200 font-medium" data-testid="user-name">
                <span>{user?.name || user?.email || ""}</span>
              </span>
            </div>
            <div
              data-testid="user-avatar"
              className="h-9 w-9 rounded-full bg-zinc-900 border border-card2-border text-zinc-100 flex items-center justify-center font-display text-sm font-bold uppercase"
              aria-label="Perfil"
              title={user?.name || user?.email || ""}
            >
              {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
            </div>
            <Button
              data-testid="change-password-btn"
              variant="ghost"
              size="icon"
              onClick={() => setPwOpen(true)}
              className="text-zinc-400 hover:text-amber-brand hover:bg-zinc-900"
              aria-label="Alterar senha"
              title="Alterar senha"
            >
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-green-brand hover:bg-zinc-900"
              aria-label="Sair"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <ThemeToggle />
          </div>
        </div>
        {isOwner && <OwnerTopNav />}
      </header>

      <div className="flex-1 flex min-w-0">
        <main data-owner-main={isOwner ? "1" : undefined} className={cn(
          "flex-1 w-full px-4 md:px-8 py-6 md:py-10 pb-24 md:pb-10 min-w-0",
          isOwner ? "lg:px-6 lg:py-6 max-w-6xl mx-auto lg:max-w-[1600px]" : "mx-auto max-w-6xl",
        )}>
          {isOwner && user?.shop?.suspended ? (
            <div data-testid="suspended-notice" className="max-w-lg mx-auto mt-16 rounded-2xl border border-amber-700 bg-amber-950/30 p-8 text-center space-y-3">
              <div className="font-display text-2xl font-black uppercase text-amber-300">Mensalidade em aberto</div>
              <p className="text-sm text-zinc-300">O acesso desta oficina está suspenso. Fale com o suporte do AutoOficina para regularizar.</p>
            </div>
          ) : (
            <>
              {isOwner && user?.impersonating && <ImpersonationBar shopName={user?.shop?.name} />}
              {isOwner && <TrialBanner shop={user?.shop} />}
              {isOwner && <NoticeBanner />}
              {isOwner && helpForRoute(location.pathname) && <div className="flex justify-end -mb-2"><HelpTip key={location.pathname} screen={helpForRoute(location.pathname)} /></div>}
              <ErrorBoundary resetKey={location.pathname}><TermsGate />{isOwner ? <ModuleGuard>{children}</ModuleGuard> : children}</ErrorBoundary>
            </>
          )}
        </main>
      </div>

      {isOwner && !user?.shop?.suspended && <AssistantFab />}

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {navItems.length > 1 && (
        <nav
          data-testid="mobile-nav"
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-appbg/95 backdrop-blur-md border-t border-card2-border pb-[env(safe-area-inset-bottom)]"
        >
          <div className="flex items-stretch justify-around">
            {navItems.map(({ to, label, icon: Icon, testid }) => {
              const active = to === "/owner" || to === "/admin"
                ? location.pathname === to
                : to === "/owner/ordens"
                ? location.pathname.startsWith("/owner/ordens") || location.pathname.startsWith("/owner/veiculo")
                : to === "/cliente"
                ? location.pathname === "/cliente" || (location.pathname.startsWith("/cliente/") && !location.pathname.startsWith("/cliente/clube"))
                : location.pathname === to || location.pathname.startsWith(to + "/");
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`${testid}-mobile`}
                  className={cn(
                    "flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-semibold uppercase tracking-wide transition-colors",
                    active ? "text-green-neon" : "text-zinc-500 hover:text-zinc-300",
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
